import { type ReactElement } from "react";

import cx from "@hsl/core/utils/cx";
import { getShareClassId } from "@hsl/fc-subscription/lib";
import { docArrSchema, type TopLevelDataObj } from "@hsl/fund-explorer/types";

import useIsEnglishLang from "../../hooks/useIsEnglishLang";
import useExplorerStore from "../../store/store";
import PDFIcon from "../PdfIcon/PdfIcon";
import { redDot } from "../Subscription/SubscriptionButtons/SubscriptionButtons";
import DocumentCheckbox from "./components/DocumentCheckbox";
import DocumentDisplay from "./components/DocumentDisplay";
import NullReplacement from "./components/NullReplacement";
import { useDocumentContext } from "./context";

export interface Props {
    factSheetOrKiid: any;
    fileName: string;
    selectionActive: boolean;
    label?: string;
    nullReplacementElem?: ReactElement;
    showLabel?: boolean;
    showName?: boolean;
    shareClass?: TopLevelDataObj;
    type: "factSheet" | "kiid" | "memberCommunication" | "esgReportSC";
}

export function FactSheetOrKiid({
    factSheetOrKiid,
    fileName,
    selectionActive,
    label,
    nullReplacementElem,
    showLabel = true,
    showName = true,
    shareClass,
    type,
}: Props) {
    const result = docArrSchema.safeParse(factSheetOrKiid);
    const productType = useExplorerStore((state) => state.config.productType);
    const getShareClass = useExplorerStore((state) => state.getShareClassById);
    const isEnglish = useIsEnglishLang();
    const showLang =
        !isEnglish ||
        (productType === "pmc" && !/^(kii?d|ESG.*)$/gi.test(label ?? ""));
    const { hasUpdates: hasUpdatesFn, markRead } = useDocumentContext();
    const scDataWithType = shareClass
        ? getShareClass(shareClass?.localId)
        : null;
    const scSubsId = scDataWithType ? getShareClassId(scDataWithType) : "";
    const hasUpdates =
        result.success &&
        shareClass &&
        hasUpdatesFn &&
        hasUpdatesFn(`${type.toLowerCase()}-${scSubsId}`);
    if (!result.success || !result.data[0]) {
        /**
         * No valid docs -> show null replacement
         */
        return nullReplacementElem ?? <NullReplacement />;
    } else if (selectionActive) {
        /**
         * Selection is active -> show checkbox for download
         */
        return (
            <label className="group flex w-full items-center justify-center">
                {showLabel && label && productType === "etf" && (
                    <span className="flex grow gap-1 group-hover:underline">
                        <PDFIcon />
                        {label}
                    </span>
                )}
                <DocumentCheckbox
                    value={result.data.map((x) => ({
                        url: x.url,
                        lang: x.lang,
                    }))}
                    name={fileName}
                />
            </label>
        );
    } else {
        return (
            <div className={cx(hasUpdates && "relative")}>
                <div className={cx(hasUpdates && redDot, "before:-right-1")}>
                    <DocumentDisplay
                        data={result.data}
                        icon={<PDFIcon />}
                        type="pdf"
                        showName={showName && productType === "etf"}
                        showLang={showLang}
                        shareClass={shareClass}
                        onClick={() =>
                            markRead?.(`${type.toLowerCase()}-${scSubsId}`)
                        }
                    />
                </div>
            </div>
        );
    }
}
